import { Coins, AlertTriangle, History } from 'lucide-react';
import { useStyleCredits } from '@/hooks/useStyleCredits';
import { cn } from '@/lib/utils';

interface StyleCreditsProps {
  size?: 'sm' | 'md' | 'lg';
  showWarning?: boolean;
  onClick?: () => void;
  onHistoryClick?: () => void;
  className?: string;
}

export function StyleCredits({ 
  size = 'md', 
  showWarning = true,
  onClick,
  onHistoryClick,
  className 
}: StyleCreditsProps) {
  const { balance, isLoading } = useStyleCredits();

  const isLowBalance = balance < 20;

  const sizeClasses = {
    sm: 'text-xs px-2.5 py-1.5 gap-1.5 min-h-[32px]',
    md: 'text-sm px-3 py-2 gap-2 min-h-[40px]',
    lg: 'text-base px-4 py-2.5 gap-2.5 min-h-[44px]'
  };

  const iconSize = {
    sm: 14,
    md: 16,
    lg: 18
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <button
        onClick={onClick}
        className={cn(
          'flex items-center rounded-full font-medium transition-all active:scale-95',
          'bg-gradient-to-r from-primary/20 to-accent/20',
          'border border-primary/30',
          'text-primary',
          'hover:from-primary/30 hover:to-accent/30',
          'hover:border-primary/50',
          sizeClasses[size],
          onClick && 'cursor-pointer'
        )}
        disabled={!onClick}
      >
        {showWarning && isLowBalance && !isLoading ? (
          <AlertTriangle size={iconSize[size]} className="text-destructive" />
        ) : (
          <Coins size={iconSize[size]} />
        )}
        <span className={cn(isLoading && 'opacity-50', 'tabular-nums font-semibold')}>
          {isLoading ? '...' : balance.toLocaleString()}
        </span>
      </button>
      
      {onHistoryClick && (
        <button
          onClick={onHistoryClick}
          className={cn(
            'p-1.5 rounded-full transition-all active:scale-95',
            'bg-muted hover:bg-muted/80',
            'text-muted-foreground hover:text-foreground'
          )}
          title="Transaction History"
        >
          <History size={iconSize[size] - 2} />
        </button>
      )}
    </div>
  );
}
